import type {
  CustomerJobEncryption,
  CustomerEncryptedPayload,
  CustomerJobEvent,
  CustomerJobPayloadEnvelope,
} from './api.js';

export async function encryptCustomerJobPayload(
  encryption: CustomerJobEncryption,
  value: unknown,
): Promise<{
  envelope: CustomerJobPayloadEnvelope;
  contentKey: Uint8Array;
}> {
  const publicKey = await crypto.subtle.importKey(
    'spki',
    toArrayBuffer(base64ToBytes(encryption.publicKey)),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
  const aes = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const rawKey = new Uint8Array(await crypto.subtle.exportKey('raw', aes));
  const encryptedKey = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    toArrayBuffer(rawKey),
  ));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    aes,
    plaintext,
  ));
  return {
    envelope: {
      version: 1,
      algorithm: 'RSA-OAEP-256+A256GCM',
      keyFingerprint: encryption.fingerprint,
      encryptedKey: bytesToBase64(encryptedKey),
      iv: bytesToBase64(iv),
      ciphertext: bytesToBase64(ciphertext),
    },
    contentKey: rawKey,
  };
}

export function rememberCustomerJobKey(jobId: string, key: Uint8Array): void {
  sessionStorage.setItem(`cyrus-job-key:${jobId}`, bytesToBase64(key));
  key.fill(0);
}

export async function decryptCustomerJobEvent(
  event: CustomerJobEvent,
): Promise<unknown | null> {
  if (!event.encryptedPayload) return null;
  const encodedKey = sessionStorage.getItem(`cyrus-job-key:${event.jobId}`);
  if (!encodedKey) return { unavailable: true };
  return decryptCustomerPayload(base64ToBytes(encodedKey), event.encryptedPayload);
}

export async function decryptCustomerPayload(
  rawKey: Uint8Array,
  encryptedPayload: CustomerEncryptedPayload,
): Promise<unknown> {
  const key = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(rawKey),
    'AES-GCM',
    false,
    ['decrypt'],
  );
  const plaintext = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: toArrayBuffer(base64ToBytes(encryptedPayload.iv)),
    },
    key,
    toArrayBuffer(base64ToBytes(encryptedPayload.ciphertext)),
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as unknown;
}

function bytesToBase64(value: Uint8Array): string {
  let binary = '';
  for (let offset = 0; offset < value.length; offset += 0x8000) {
    binary += String.fromCharCode(...value.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function toArrayBuffer(value: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy.buffer;
}
